import axios from 'axios';
import { buildConsentPayload } from './consentPayload';
import { ensureArray } from './utils';
import { getSourceUrlWithUtm, getUtmPayloadForApi } from './utmContext';
import { isAdminApiPath } from './adminApiPaths';

// Базовый URL API - можно вынести в переменные окружения
// Используем относительный путь по умолчанию для продакшена
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Создаем экземпляр axios с базовой конфигурацией
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем interceptor для автоматической установки токена
// Важно: adminToken только для запросов к /admin/... Иначе просроченный admin JWT
// подменяет пользовательский токен → 401 на /cart, /auth/me и «Not authenticated» после логина.
apiClient.interceptors.request.use(
  (config) => {
    if ((config.method || 'get').toLowerCase() === 'get') {
      config.headers = config.headers || {};
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }
    const token = localStorage.getItem('token');
    const adminToken = localStorage.getItem('adminToken');
    const base = (config.baseURL || apiClient.defaults.baseURL || '').toString();
    const path = (config.url || '').toString();
    const pathOnly = path.split('?')[0];
    const fullPath = `${base}${path}`.split('?')[0];
    const isAdminApi = isAdminApiPath(pathOnly) || isAdminApiPath(fullPath);

    if (adminToken && isAdminApi) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Добавляем interceptor для обработки ошибок авторизации
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status !== 401 && status !== 403) {
      return Promise.reject(error);
    }
    const url = error.config?.url || '';
    if (
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/password-reset') ||
      url.includes('/admin/login')
    ) {
      return Promise.reject(error);
    }

    const headers = error.config?.headers || {};
    const authHeader =
      headers.Authorization ?? headers.authorization ?? '';
    const reqToken =
      typeof authHeader === 'string' && authHeader.startsWith('Bearer ')
        ? authHeader.slice(7).trim()
        : '';

    const base = (error.config?.baseURL || apiClient.defaults.baseURL || '').toString();
    const pathOnly = url.split('?')[0];
    const fullPath = `${base}${url}`.split('?')[0];
    const isAdminApi = isAdminApiPath(pathOnly) || isAdminApiPath(fullPath);

    const lsUserToken = localStorage.getItem('token');
    const lsAdminToken = localStorage.getItem('adminToken');

    // Запрос ушёл со старым user JWT, а в LS уже новый (логин / другая вкладка) — не затираем сессию
    if (!isAdminApi && reqToken && lsUserToken && reqToken !== lsUserToken) {
      return Promise.reject(error);
    }
    if (isAdminApi && reqToken && lsAdminToken && reqToken !== lsAdminToken) {
      return Promise.reject(error);
    }
    // 403 «Not authenticated» без Bearer в конфиге при живом токене в LS — часто гонка с Header после логина
    if (!reqToken && lsUserToken && !isAdminApi) {
      return Promise.reject(error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth-tokens-cleared'));
    }
    return Promise.reject(error);
  }
);

// API для заказа звонка
export const callbackAPI = {
  /**
   * Отправить заявку на обратный звонок
   * @param {string} phone - Номер телефона
   * @param {string} source_url - URL страницы откуда заявка (опционально)
   * @returns {Promise} Promise с данными заявки
   */
  createCallback: async (contact, source_url = null, options = {}) => {
    try {
      const payload = {
        source_url: source_url || getSourceUrlWithUtm(),
        utm: getUtmPayloadForApi(),
        ...buildConsentPayload(options.consent, options.marketingConsent),
      };
      if (options.phone) payload.phone = options.phone;
      if (options.email) payload.email = options.email;
      if (!payload.phone && !payload.email && contact) {
        if (options.contactType === 'email') payload.email = contact;
        else payload.phone = contact;
      }
      if (options.companyWebsite) payload.company_website = options.companyWebsite;
      if (options.question) payload.question = options.question;
      if (options.captchaToken) payload.ddg_captcha_token = options.captchaToken;
      const response = await apiClient.post('/callback', payload);
      return response.data;
    } catch (error) {
      // Обработка ошибок
      if (error.response) {
        // Сервер вернул ошибку
        throw new Error(error.response.data?.detail || 'Ошибка при отправке заявки');
      } else if (error.request) {
        // Запрос был сделан, но ответ не получен
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        // Что-то пошло не так при настройке запроса
        throw new Error('Ошибка при отправке заявки');
      }
    }
  },
};

// API для запроса расчёта
export const calculationAPI = {
  /**
   * Отправить запрос на расчёт стоимости
   * @param {string} contact - Телефон или email
   * @param {string} contactType - Тип контакта: 'phone' или 'email'
   * @param {Object} calculatorData - Данные калькулятора (опционально)
   * @param {string} contactName - Имя контактного лица (опционально)
   * @param {string} source_url - URL страницы откуда заявка (опционально)
   * @returns {Promise} Promise с результатом
   */
  createCalculationRequest: async (
    contact,
    contactType,
    calculatorData = null,
    contactName = null,
    source_url = null,
    consent = false,
    marketingConsent = false,
    question = null,
    captchaToken = null,
  ) => {
    try {
      const payload = {
        contact,
        contact_type: contactType,
        calculator_data: calculatorData,
        contact_name: contactName,
        source_url: source_url || getSourceUrlWithUtm(),
        utm: getUtmPayloadForApi(),
        ...buildConsentPayload(consent, marketingConsent),
      };
      if (question) payload.question = question;
      if (captchaToken) payload.ddg_captcha_token = captchaToken;
      const response = await apiClient.post('/calculation-request', payload);
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при отправке запроса на расчёт');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при отправке запроса на расчёт');
      }
    }
  },
};

// API для квизов
export const quizAPI = {
  /**
   * Отправить результаты квиза
   * @param {string} phone - Номер телефона
   * @param {Object} quizData - Данные квиза (ответы на вопросы)
   * @param {string} contactName - Имя контактного лица (опционально)
   * @param {string} contactEmail - Email контактного лица (опционально)
   * @param {string} source_url - URL страницы откуда заявка (опционально)
   * @param {string} companyWebsite - Сайт компании
   * @returns {Promise} Promise с результатом
   */
  submitQuiz: async (
    phone,
    quizData,
    contactName = null,
    contactEmail = null,
    source_url = null,
    companyWebsite = null,
    consent = false,
    marketingConsent = false,
    question = null,
    captchaToken = null,
  ) => {
    try {
      const payload = {
        phone: phone || null,
        quiz_data: quizData,
        contact_name: contactName,
        contact_email: contactEmail,
        source_url: source_url || getSourceUrlWithUtm(),
        utm: getUtmPayloadForApi(),
        company_website: companyWebsite,
        ...buildConsentPayload(consent, marketingConsent),
      };
      if (question) payload.question = question;
      if (captchaToken) payload.ddg_captcha_token = captchaToken;
      const response = await apiClient.post('/quiz-submission', payload);
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при отправке результатов квиза');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при отправке результатов квиза');
      }
    }
  },
};

// API для продуктов
export const productsAPI = {
  /**
   * Получить список продуктов.
   * view=catalog + limit → { items, total, limit, offset }.
   * Legacy массив (без view / limit=0) → оборачивается в { items, total }.
   * @returns {Promise<{ items: Array, total: number, limit?: number, offset?: number }|Array>}
   */
  getProducts: async (options = {}) => {
    try {
      const params = {};
      if (options.view) params.view = options.view;
      if (options.limit !== undefined && options.limit !== null) params.limit = options.limit;
      if (options.offset !== undefined && options.offset !== null) params.offset = options.offset;
      if (options.vendor_slug) params.vendor_slug = options.vendor_slug;
      if (options.category && options.category !== 'all') params.category = options.category;
      if (options.section_id) params.section_id = options.section_id;
      if (options.section_slug) params.section_slug = options.section_slug;
      if (options.situation_id !== undefined && options.situation_id !== null) {
        params.situation_id = options.situation_id;
      }
      if (options.q) params.q = options.q;
      if (options.sort) params.sort = options.sort;

      const response = await apiClient.get('/products', { params });
      const data = response.data;
      const wantPage =
        options.view === 'catalog' && options.limit !== 0 && options.limit != null;

      if (wantPage) {
        if (data && Array.isArray(data.items)) {
          return {
            items: data.items,
            total: Number(data.total) || data.items.length,
            limit: Number(data.limit) || options.limit,
            offset: Number(data.offset) || options.offset || 0,
          };
        }
        const arr = ensureArray(data);
        return {
          items: arr,
          total: arr.length,
          limit: options.limit,
          offset: options.offset || 0,
        };
      }

      if (data && Array.isArray(data.items)) {
        return data.items;
      }
      return ensureArray(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении продуктов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении продуктов');
      }
    }
  },

  /**
   * Получить продукт по ID
   * @param {string} productId - ID продукта
   * @returns {Promise<Object>} Promise с данными продукта
   */
  getProduct: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      if (error.response) {
        if (error.response.status === 404) {
          throw new Error('Продукт не найден');
        }
        throw new Error(error.response.data?.detail || 'Ошибка при получении продукта');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении продукта');
      }
    }
  },

  /**
   * Отправить заявку на КП
   * @param {Object} data - Данные заявки
   * @returns {Promise<Object>} Promise с результатом
   */
  submitRequest: async (data) => {
    try {
      const response = await apiClient.post('/requests', data);
      return response.data;
    } catch (error) {
      console.error('Error submitting request:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при отправке заявки');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при отправке заявки');
      }
    }
  },
};

// API для категорий
export const categoriesAPI = {
  /**
   * Получить список всех категорий
   * @returns {Promise<Array>} Promise с массивом категорий
   */
  getCategories: async () => {
    try {
      const response = await apiClient.get('/categories');
      return ensureArray(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении категорий');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении категорий');
      }
    }
  },
};

// Разделы каталога (привязка к категории; slug в URL /catalog/:category/:slug)
export const sectionsAPI = {
  getSections: async (categoryId = null) => {
    try {
      const response = await apiClient.get('/sections', {
        params: categoryId ? { category_id: categoryId } : {},
      });
      return ensureArray(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении разделов');
    }
  },
};

// API для ситуаций
export const situationsAPI = {
  /**
   * Получить список всех ситуаций
   * @returns {Promise<Array>} Promise с массивом ситуаций
   */
  getSituations: async () => {
    try {
      const response = await apiClient.get('/situations');
      return ensureArray(response.data);
    } catch (error) {
      console.error('Error fetching situations:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении ситуаций');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении ситуаций');
      }
    }
  },
};

// API для профиля компании
export const passwordResetAPI = {
  requestCode: async (email) => {
    try {
      const response = await apiClient.post('/auth/password-reset/request', { email });
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(
        typeof detail === 'string' ? detail : 'Не удалось отправить код'
      );
    }
  },
  confirm: async ({ email, code, new_password }) => {
    try {
      const response = await apiClient.post('/auth/password-reset/confirm', {
        email,
        code,
        new_password,
      });
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        throw new Error(detail.map((item) => item.msg || item).join('; '));
      }
      throw new Error(
        typeof detail === 'string' ? detail : 'Не удалось сменить пароль'
      );
    }
  },
};

export const profileAPI = {
  /**
   * Получить профиль компании
   * @returns {Promise<Object>} Promise с данными профиля
   */
  getProfile: async () => {
    try {
      const response = await apiClient.get('/profile');
      console.log('Profile response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      console.error('Error response:', error.response);
      if (error.response) {
        // Если профиль не найден (404), возвращаем null вместо ошибки
        if (error.response.status === 404) {
          return null;
        }
        throw new Error(error.response.data?.detail || 'Ошибка при получении профиля');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении профиля');
      }
    }
  },

  /**
   * Обновить профиль компании
   * @param {Object} profileData - Данные профиля
   * @returns {Promise<Object>} Promise с обновленными данными профиля
   */
  updateProfile: async (profileData) => {
    try {
      const response = await apiClient.put('/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при обновлении профиля');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при обновлении профиля');
      }
    }
  },
};

// API для корзины
export const cartAPI = {
  /**
   * Получить корзину пользователя
   * @returns {Promise<Array>} Promise с массивом товаров в корзине
   */
  getCart: async () => {
    try {
      const response = await apiClient.get('/cart');
      return response.data;
    } catch (error) {
      console.error('Error fetching cart:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении корзины');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении корзины');
      }
    }
  },

  /**
   * Добавить товар в корзину
   * @param {string} productId - ID продукта
   * @param {Object} calculatorData - Данные калькулятора (опционально)
   * @returns {Promise<Object>} Promise с данными добавленного товара
   */
  addToCart: async (productId, calculatorData = null) => {
    try {
      const payload = { 
        product_id: productId
      };
      if (calculatorData) {
        payload.calculator_data = calculatorData;
      }
      
      console.log('Adding to cart:', payload);
      const response = await apiClient.post('/cart', payload);
      console.log('Cart response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      console.error('Error response:', error.response?.data);
      if (error.response) {
        const errorMessage = error.response.data?.detail || 'Ошибка при добавлении в корзину';
        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при добавлении в корзину');
      }
    }
  },

  /**
   * Удалить товар из корзины
   * @param {string} itemId - ID товара в корзине
   * @returns {Promise} Promise с результатом удаления
   */
  removeFromCart: async (itemId) => {
    try {
      const response = await apiClient.delete(`/cart/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing from cart:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при удалении из корзины');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при удалении из корзины');
      }
    }
  },

  /**
   * Обновить количество товара в корзине
   * @param {string} itemId - ID товара в корзине
   * @param {number} quantity - Новое количество
   * @param {string} notes - Примечания (опционально)
   */
  updateQuantity: async (itemId, quantity, notes = null) => {
    try {
      const response = await apiClient.put(`/cart/${itemId}`, { quantity, notes });
      return response.data;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при обновлении');
    }
  },

  /**
   * Получить итоги корзины
   */
  getSummary: async () => {
    try {
      const response = await apiClient.get('/cart/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching cart summary:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении итогов');
    }
  },

  // Guest cart methods
  getGuestCart: async (sessionId) => {
    try {
      const response = await apiClient.get('/cart/guest', { params: { session_id: sessionId } });
      return response.data;
    } catch (error) {
      console.error('Error fetching guest cart:', error);
      return { items: [], total_items: 0, total_quantity: 0 };
    }
  },

  addToGuestCart: async (productId, calculatorData, sessionId) => {
    try {
      const response = await apiClient.post('/cart/guest', {
        product_id: productId,
        calculator_data: calculatorData,
        session_id: sessionId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding to guest cart:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при добавлении');
    }
  },

  removeFromGuestCart: async (itemId, sessionId) => {
    try {
      const response = await apiClient.delete(`/cart/guest/${itemId}`, { params: { session_id: sessionId } });
      return response.data;
    } catch (error) {
      console.error('Error removing from guest cart:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении');
    }
  },

  updateGuestCartItem: async (itemId, sessionId, quantity, notes = null) => {
    try {
      const response = await apiClient.put(`/cart/guest/${itemId}`, { 
        session_id: sessionId, 
        quantity, 
        notes 
      });
      return response.data;
    } catch (error) {
      console.error('Error updating guest cart:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при обновлении');
    }
  }
};

// API для покупок
export const purchasesAPI = {
  /**
   * Получить список покупок пользователя
   * @returns {Promise<Array>} Promise с массивом покупок
   */
  getPurchases: async () => {
    try {
      const response = await apiClient.get('/purchases');
      return response.data;
    } catch (error) {
      console.error('Error fetching purchases:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении покупок');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении покупок');
      }
    }
  },

  /**
   * Создать покупку
   * @param {Object} purchaseData - Данные покупки
   * @returns {Promise<Object>} Promise с данными созданной покупки
   */
  createPurchase: async (purchaseData) => {
    try {
      const response = await apiClient.post('/purchases', purchaseData);
      return response.data;
    } catch (error) {
      console.error('Error creating purchase:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при создании покупки');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при создании покупки');
      }
    }
  },
};

// API для заказов
export const ordersAPI = {
  /**
   * Создать заказ из корзины
   * @param {Object} orderData - Данные заказа
   * @returns {Promise<Object>} Promise с данными созданного заказа
   */
  createOrder: async (orderData, consent = false, marketingConsent = false) => {
    try {
      const response = await apiClient.post('/orders', {
        ...orderData,
        ...buildConsentPayload(consent, marketingConsent),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating order:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при создании заказа');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при создании заказа');
      }
    }
  },
};

// API для guest checkout
export const checkoutAPI = {
  /**
   * Создать заказ через guest checkout
   * @param {Object} checkoutData - Данные checkout
   */
  createOrder: async (checkoutData, consent = false, marketingConsent = false) => {
    try {
      const response = await apiClient.post('/checkout', {
        ...checkoutData,
        ...buildConsentPayload(consent, marketingConsent),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating checkout order:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при оформлении заказа');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при оформлении заказа');
      }
    }
  },
};

// API для администратора
export const adminAPI = {
  /**
   * Вход администратора
   * @param {string} username - Имя пользователя
   * @param {string} password - Пароль
   * @returns {Promise<Object>} Promise с токеном администратора
   */
  login: async (username, password) => {
    try {
      const response = await apiClient.post('/admin/login', { username, password });
      return response.data;
    } catch (error) {
      console.error('Error admin login:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при входе');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при входе');
      }
    }
  },

  /**
   * Получить все продукты (для админ-панели)
   * @returns {Promise<Array>} Promise с массивом продуктов
   */
  getProducts: async () => {
    try {
      const response = await apiClient.get('/admin/products');
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении продуктов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении продуктов');
      }
    }
  },

  /**
   * Создать продукт
   * @param {Object} productData - Данные продукта
   * @returns {Promise<Object>} Promise с данными созданного продукта
   */
  createProduct: async (productData) => {
    try {
      const response = await apiClient.post('/admin/products', productData);
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при создании продукта');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при создании продукта');
      }
    }
  },

  /**
   * Импортировать продукты из CSV/XLSX
   * @param {File} file - Файл импорта
   * @returns {Promise<Object>} Отчет импорта
   */
  importProducts: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/admin/products/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error importing products:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при импорте продуктов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при импорте продуктов');
      }
    }
  },

  /**
   * Выгрузить все решения в CSV (совместимо с шаблоном импорта, без icon)
   * @returns {Promise<Blob>} CSV-файл
   */
  exportProductsCsv: async () => {
    try {
      const response = await apiClient.get('/admin/products/export', {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting products CSV:', error);
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          throw new Error(parsed.detail || 'Ошибка при выгрузке решений');
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message !== 'Ошибка при выгрузке решений') {
            throw parseErr;
          }
        }
      }
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при выгрузке решений');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при выгрузке решений');
      }
    }
  },

  /**
   * Обновить продукт
   * @param {string} productId - ID продукта
   * @param {Object} productData - Данные продукта
   * @returns {Promise<Object>} Promise с обновленными данными продукта
   */
  updateProduct: async (productId, productData) => {
    try {
      const response = await apiClient.put(`/admin/products/${productId}`, productData);
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при обновлении продукта');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при обновлении продукта');
      }
    }
  },

  /**
   * Удалить продукт
   * @param {string} productId - ID продукта
   * @returns {Promise} Promise с результатом удаления
   */
  deleteProduct: async (productId) => {
    try {
      const response = await apiClient.delete(`/admin/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при удалении продукта');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при удалении продукта');
      }
    }
  },

  /**
   * Загрузить файл для продукта
   * @param {string} productId - ID продукта
   * @param {File} file - Файл для загрузки
   * @returns {Promise<Object>} Promise с данными загруженного файла
   */
  uploadProductFile: async (productId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/admin/products/${productId}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при загрузке файла');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при загрузке файла');
      }
    }
  },

  /**
   * Получить файлы продукта
   * @param {string} productId - ID продукта
   * @returns {Promise<Array>} Promise с массивом файлов
   */
  getProductFiles: async (productId) => {
    try {
      const response = await apiClient.get(`/admin/products/${productId}/files`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product files:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении файлов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении файлов');
      }
    }
  },

  /**
   * Удалить файл продукта
   * @param {string} productId - ID продукта
   * @param {string} fileId - ID файла
   * @returns {Promise} Promise с результатом удаления
   */
  deleteProductFile: async (productId, fileId) => {
    try {
      const response = await apiClient.delete(`/admin/products/${productId}/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при удалении файла');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при удалении файла');
      }
    }
  },

  /**
   * Получить заказ по ID
   * @param {string} orderId - ID заказа
   * @returns {Promise<Object>} Promise с данными заказа
   */
  getOrder: async (orderId) => {
    try {
      const response = await apiClient.get(`/admin/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении заказа');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении заказа');
      }
    }
  },

  /**
   * Получить заказ по номеру заказа
   * @param {string} orderNumber - Номер заказа
   * @returns {Promise<Object>} Promise с данными заказа
   */
  getOrderByNumber: async (orderNumber) => {
    try {
      const response = await apiClient.get(`/admin/orders/by-number/${orderNumber}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order by number:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении заказа');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении заказа');
      }
    }
  },

  /**
   * Получить все заказы
   * @param {string} status - Фильтр по статусу (опционально)
   * @returns {Promise<Array>} Promise с массивом заказов
   */
  getOrders: async (status = null) => {
    try {
      const url = status ? `/admin/orders?status=${status}` : '/admin/orders';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении заказов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении заказов');
      }
    }
  },

  /**
   * Получить заказы в корзине
   * @returns {Promise<Array>} Promise с массивом заказов в корзине
   */
  getCartOrders: async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      console.log('Fetching cart orders from API...');
      console.log('Admin token exists:', !!adminToken);
      console.log('API base URL:', apiClient.defaults.baseURL);
      console.log('Full URL will be:', `${apiClient.defaults.baseURL}/admin/orders/cart`);
      
      const response = await apiClient.get('/admin/orders/cart');
      console.log('Cart orders API response status:', response.status);
      console.log('Cart orders API response:', response.data);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching cart orders:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers
        }
      });
      if (error.response) {
        // 401 means not authenticated, 404 means endpoint not found
        if (error.response.status === 401) {
          throw new Error('Не авторизован. Пожалуйста, войдите в систему как администратор.');
        } else if (error.response.status === 404) {
          throw new Error('Эндпоинт не найден. Проверьте правильность URL.');
        }
        throw new Error(error.response.data?.detail || `Ошибка при получении заказов в корзине (${error.response.status})`);
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении заказов в корзине');
      }
    }
  },

  /**
   * Получить покупки
   * @returns {Promise<Array>} Promise с массивом покупок
   */
  getPurchases: async () => {
    try {
      const response = await apiClient.get('/admin/orders/purchases');
      return response.data;
    } catch (error) {
      console.error('Error fetching purchases:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении покупок');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении покупок');
      }
    }
  },

  /**
   * Обновить статус заказа
   * @param {string} orderId - ID заказа
   * @param {string} status - Новый статус
   * @returns {Promise} Promise с результатом обновления
   */
  updateOrderStatus: async (orderId, status) => {
    try {
      const response = await apiClient.put(`/admin/orders/${orderId}/status`, {
        new_status: status,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating order status:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при обновлении статуса заказа');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при обновлении статуса заказа');
      }
    }
  },
};

// API для лендингов комплексных решений
export const landingsAPI = {
  /**
   * Получить активные лендинги (публичный endpoint)
   * @returns {Promise<Array>} Promise с массивом активных лендингов
   */
  getActiveLandings: async () => {
    try {
      const response = await apiClient.get('/landings');
      return response.data;
    } catch (error) {
      console.error('Error fetching active landings:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении лендингов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении лендингов');
      }
    }
  },

  /**
   * Получить все лендинги (для админ-панели)
   * @returns {Promise<Array>} Promise с массивом лендингов
   */
  getLandings: async () => {
    try {
      const response = await apiClient.get('/admin/landings');
      return response.data;
    } catch (error) {
      console.error('Error fetching landings:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении лендингов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении лендингов');
      }
    }
  },

  /**
   * Получить лендинг по ID
   * @param {string} landingId - ID лендинга
   * @returns {Promise<Object>} Promise с данными лендинга
   */
  getLanding: async (landingId) => {
    try {
      const response = await apiClient.get(`/admin/landings/${landingId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching landing:', error);
      if (error.response) {
        if (error.response.status === 404) {
          throw new Error('Лендинг не найден');
        }
        throw new Error(error.response.data?.detail || 'Ошибка при получении лендинга');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении лендинга');
      }
    }
  },

  /**
   * Создать лендинг
   * @param {Object} landingData - Данные лендинга
   * @returns {Promise<Object>} Promise с данными созданного лендинга
   */
  createLanding: async (landingData) => {
    try {
      const response = await apiClient.post('/admin/landings', landingData);
      return response.data;
    } catch (error) {
      console.error('Error creating landing:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при создании лендинга');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при создании лендинга');
      }
    }
  },

  /**
   * Обновить лендинг
   * @param {string} landingId - ID лендинга
   * @param {Object} landingData - Данные лендинга
   * @returns {Promise<Object>} Promise с обновленными данными лендинга
   */
  updateLanding: async (landingId, landingData) => {
    try {
      const response = await apiClient.put(`/admin/landings/${landingId}`, landingData);
      return response.data;
    } catch (error) {
      console.error('Error updating landing:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при обновлении лендинга');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при обновлении лендинга');
      }
    }
  },

  /**
   * Удалить лендинг
   * @param {string} landingId - ID лендинга
   * @returns {Promise} Promise с результатом удаления
   */
  deleteLanding: async (landingId) => {
    try {
      const response = await apiClient.delete(`/admin/landings/${landingId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting landing:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при удалении лендинга');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при удалении лендинга');
      }
    }
  },

  /**
   * Загрузить файл для лендинга
   * @param {string} landingId - ID лендинга
   * @param {File} file - Файл для загрузки
   * @returns {Promise<Object>} Promise с данными загруженного файла
   */
  uploadLandingFile: async (landingId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/admin/landings/${landingId}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при загрузке файла');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при загрузке файла');
      }
    }
  },

  /**
   * Получить файлы лендинга
   * @param {string} landingId - ID лендинга
   * @returns {Promise<Array>} Promise с массивом файлов
   */
  getLandingFiles: async (landingId) => {
    try {
      const response = await apiClient.get(`/admin/landings/${landingId}/files`);
      return response.data;
    } catch (error) {
      console.error('Error fetching landing files:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при получении файлов');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при получении файлов');
      }
    }
  },

  /**
   * Удалить файл лендинга
   * @param {string} landingId - ID лендинга
   * @param {string} fileId - ID файла
   * @returns {Promise} Promise с результатом удаления
   */
  deleteLandingFile: async (landingId, fileId) => {
    try {
      const response = await apiClient.delete(`/admin/landings/${landingId}/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Ошибка при удалении файла');
      } else if (error.request) {
        throw new Error('Не удалось подключиться к серверу. Проверьте подключение к интернету.');
      } else {
        throw new Error('Ошибка при удалении файла');
      }
    }
  },
};

// API для решений на главной странице
export const homeSolutionsAPI = {
  /**
   * Получить активные решения для главной
   */
  getActive: async () => {
    try {
      const response = await apiClient.get('/home-solutions');
      return response.data;
    } catch (error) {
      console.error('Error fetching home solutions:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении решений');
    }
  },

  /**
   * Получить все решения (админ)
   */
  getAll: async () => {
    try {
      const response = await apiClient.get('/admin/home-solutions');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin home solutions:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении решений');
    }
  },

  create: async (data) => {
    try {
      const response = await apiClient.post('/admin/home-solutions', data);
      return response.data;
    } catch (error) {
      console.error('Error creating home solution:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при создании решения');
    }
  },

  update: async (id, data) => {
    try {
      const response = await apiClient.put(`/admin/home-solutions/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating home solution:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при обновлении решения');
    }
  },

  remove: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/home-solutions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting home solution:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении решения');
    }
  },
};

// API для ситуаций (фильтры решений) — админ
export const adminCategoriesAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/admin/categories');
      return ensureArray(response.data);
    } catch (error) {
      console.error('Error fetching admin categories:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении категорий');
    }
  },
  create: async (data) => {
    try {
      const response = await apiClient.post('/admin/categories', data);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при создании категории');
    }
  },
  update: async (id, data) => {
    try {
      const response = await apiClient.put(`/admin/categories/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при обновлении категории');
    }
  },
  remove: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении категории');
    }
  },
};

export const adminSectionsAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/admin/sections');
      return ensureArray(response.data);
    } catch (error) {
      console.error('Error fetching admin sections:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении разделов');
    }
  },
  create: async (data) => {
    try {
      const response = await apiClient.post('/admin/sections', data);
      return response.data;
    } catch (error) {
      console.error('Error creating section:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при создании раздела');
    }
  },
  update: async (id, data) => {
    try {
      const response = await apiClient.put(`/admin/sections/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating section:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при обновлении раздела');
    }
  },
  remove: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/sections/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting section:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении раздела');
    }
  },
};

export const adminSituationsAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/admin/situations');
      return response.data;
    } catch (error) {
      console.error('Error fetching situations:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении ситуаций');
    }
  },
  create: async (data) => {
    try {
      const response = await apiClient.post('/admin/situations', data);
      return response.data;
    } catch (error) {
      console.error('Error creating situation:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при создании ситуации');
    }
  },
  update: async (id, data) => {
    try {
      const response = await apiClient.put(`/admin/situations/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating situation:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при обновлении ситуации');
    }
  },
  remove: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/situations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting situation:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении ситуации');
    }
  },
};

export const vendorsAPI = {
  getVendors: async (options = {}) => {
    try {
      const params = {};
      if (options.view) params.view = options.view;
      const response = await apiClient.get('/vendors', { params });
      return ensureArray(response.data);
    } catch (error) {
      const detail =
        error?.response?.data?.detail ??
        error?.response?.data?.message ??
        error?.message;
      console.error('Error fetching vendors:', detail || error);
      throw new Error(
        typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : 'Ошибка при получении вендоров',
      );
    }
  },
  getVendor: async (slug) => {
    try {
      const response = await apiClient.get(`/vendors/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor:', error);
      if (error.response?.status === 404) {
        throw new Error('Вендор не найден');
      }
      throw new Error(error.response?.data?.detail || 'Ошибка при получении вендора');
    }
  },
};

export const adminVendorsAPI = {
  getVendors: async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await apiClient.get('/admin/vendors', {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении вендоров');
    }
  },
  create: async (vendorData) => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await apiClient.post('/admin/vendors', vendorData, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating vendor:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при создании вендора');
    }
  },
  update: async (id, vendorData) => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await apiClient.put(`/admin/vendors/${id}`, vendorData, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating vendor:', error?.response?.data || error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const loc = Array.isArray(first?.loc) ? first.loc.join('.') : '';
        const msg = first?.msg || 'Ошибка валидации';
        throw new Error(loc ? `${loc}: ${msg}` : `${msg} (${JSON.stringify(first)})`);
      }
      throw new Error(
        error.response?.data
          ? JSON.stringify(error.response.data)
          : detail || 'Ошибка при обновлении вендора'
      );
    }
  },
  delete: async (id) => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await apiClient.delete(`/admin/vendors/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting vendor:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении вендора');
    }
  },
  syncFromProducts: async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await apiClient.post(
        '/admin/vendors/sync-from-products',
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      return response.data;
    } catch (error) {
      console.error('Error syncing vendors from products:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка синхронизации вендоров');
    }
  },
};

export const newsAPI = {
  getPublications: async (category, { featuredOnly = false } = {}) => {
    try {
      const params = {};
      if (category && category !== 'all') params.category = category;
      if (featuredOnly) params.featured_only = true;
      const response = await apiClient.get('/news/publications', { params });
      return ensureArray(response.data);
    } catch (error) {
      console.error('Error fetching news publications:', error);
      return [];
    }
  },
  getPublication: async (slug) => {
    try {
      const response = await apiClient.get(`/news/publications/${encodeURIComponent(slug)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching news publication:', error);
      if (error.response?.status === 404) return null;
      throw new Error(error.response?.data?.detail || 'Ошибка при получении публикации');
    }
  },
};

export const adminNewsAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/admin/news');
      return ensureArray(response.data);
    } catch (error) {
      console.error('Error fetching admin news:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при получении новостей');
    }
  },
  create: async (payload) => {
    try {
      const response = await apiClient.post('/admin/news', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating news:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при создании публикации');
    }
  },
  update: async (id, payload) => {
    try {
      const response = await apiClient.put(`/admin/news/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating news:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при обновлении публикации');
    }
  },
  remove: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/news/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting news:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении публикации');
    }
  },
};

export const mediaAPI = {
  upload: async (file, folder = 'vendors') => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        throw new Error('Not authenticated');
      }
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/media/upload?folder=${encodeURIComponent(folder)}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${adminToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при загрузке медиафайла');
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get('/media');
      return response.data;
    } catch (error) {
      console.error('Error getting media:', error);
      return [];
    }
  },
  remove: async (url) => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        throw new Error('Not authenticated');
      }
      const response = await apiClient.delete('/admin/media', {
        params: { url },
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting media:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка при удалении медиафайла');
    }
  },
};

async function subscriptionError(error, fallback) {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return new Error(parsed.detail || fallback);
    } catch {
      return new Error(fallback);
    }
  }
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    return new Error(detail.map((item) => item.msg).filter(Boolean).join('; ') || fallback);
  }
  return new Error(detail || fallback);
}

function saveSubscriptionFile(response, fallbackName) {
  const header = response.headers?.['content-disposition'] || '';
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = header.match(/filename="([^"]+)"/i);
  const filename = encoded ? decodeURIComponent(encoded[1]) : (plain?.[1] || fallbackName);
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const adminSubscriptionsAPI = {
  getCompanies: async () => {
    try {
      const response = await apiClient.get('/admin/subscriptions/companies');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить компании');
    }
  },
  saveCompany: async (id, payload) => {
    try {
      const response = id
        ? await apiClient.put(`/admin/subscriptions/companies/${id}`, payload)
        : await apiClient.post('/admin/subscriptions/companies', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить компанию');
    }
  },
  deleteCompany: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/subscriptions/companies/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить компанию');
    }
  },
  searchUsers: async (q = '') => {
    try {
      const response = await apiClient.get('/admin/subscriptions/users', { params: { q } });
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось найти пользователей ЛК');
    }
  },
  getClients: async () => {
    try {
      const response = await apiClient.get('/admin/subscriptions/clients');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить клиентов');
    }
  },
  saveClient: async (id, payload) => {
    try {
      const response = id
        ? await apiClient.put(`/admin/subscriptions/clients/${id}`, payload)
        : await apiClient.post('/admin/subscriptions/clients', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить клиента');
    }
  },
  deleteClient: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/subscriptions/clients/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить клиента');
    }
  },
  getPayments: async () => {
    try {
      const response = await apiClient.get('/admin/subscriptions/payments');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить платежи');
    }
  },
  savePayment: async (id, payload) => {
    try {
      const response = id
        ? await apiClient.put(`/admin/subscriptions/payments/${id}`, payload)
        : await apiClient.post('/admin/subscriptions/payments', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить платёж');
    }
  },
  deletePayment: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/subscriptions/payments/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить платёж');
    }
  },
  getPartners: async () => {
    try {
      const response = await apiClient.get('/admin/subscriptions/partners');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить партнёров');
    }
  },
  savePartner: async (id, payload) => {
    try {
      const response = id
        ? await apiClient.put(`/admin/subscriptions/partners/${id}`, payload)
        : await apiClient.post('/admin/subscriptions/partners', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить партнёра');
    }
  },
  deletePartner: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/subscriptions/partners/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить партнёра');
    }
  },
  getServices: async () => {
    try {
      const response = await apiClient.get('/admin/subscriptions/services');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить сервисы');
    }
  },
  saveService: async (id, payload) => {
    try {
      const response = id
        ? await apiClient.put(`/admin/subscriptions/services/${id}`, payload)
        : await apiClient.post('/admin/subscriptions/services', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить сервис');
    }
  },
  deleteService: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/subscriptions/services/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить сервис');
    }
  },
  getTariffs: async () => {
    try {
      const response = await apiClient.get('/admin/subscriptions/tariffs');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить тарифы');
    }
  },
  saveTariff: async (id, payload) => {
    try {
      const response = id
        ? await apiClient.put(`/admin/subscriptions/tariffs/${id}`, payload)
        : await apiClient.post('/admin/subscriptions/tariffs', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить тариф');
    }
  },
  deleteTariff: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/subscriptions/tariffs/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить тариф');
    }
  },
  getCharges: async (params) => {
    try {
      const response = await apiClient.get('/admin/subscriptions/charges', { params });
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить списания');
    }
  },
  saveCharge: async (id, payload) => {
    try {
      const response = id
        ? await apiClient.put(`/admin/subscriptions/charges/${id}`, payload)
        : await apiClient.post('/admin/subscriptions/charges', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить списание');
    }
  },
  deleteCharge: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/subscriptions/charges/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить списание');
    }
  },
  getTrash: async () => {
    try {
      const response = await apiClient.get('/admin/subscriptions/trash');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить корзину');
    }
  },
  restoreTrashItem: async (entityType, id) => {
    try {
      const response = await apiClient.post(
        `/admin/subscriptions/trash/${entityType}/${id}/restore`
      );
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось восстановить запись');
    }
  },
  exportClient: async (params) => {
    try {
      const search = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== '') {
              search.append(key, String(item));
            }
          });
          return;
        }
        search.append(key, String(value));
      });
      const response = await apiClient.get(
        `/admin/subscriptions/export/client?${search.toString()}`,
        { responseType: 'blob' }
      );
      saveSubscriptionFile(response, 'client.xlsx');
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось выгрузить файл для клиента');
    }
  },
  exportPartner: async (params) => {
    try {
      const response = await apiClient.get('/admin/subscriptions/export/partner', {
        params,
        responseType: 'blob',
      });
      saveSubscriptionFile(response, 'partner.xlsx');
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось выгрузить файл для партнёра');
    }
  },
};

export const cabinetSubscriptionsAPI = {
  getCharges: async () => {
    try {
      const response = await apiClient.get('/cabinet/subscriptions/charges');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить лицензии');
    }
  },
  getClients: async () => {
    try {
      const response = await apiClient.get('/cabinet/subscriptions/clients');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить клиентов');
    }
  },
  getServices: async () => {
    try {
      const response = await apiClient.get('/cabinet/subscriptions/services');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить сервисы');
    }
  },
  resolveCatalog: async (payload) => {
    try {
      const response = await apiClient.post('/cabinet/subscriptions/resolve-catalog', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось выбрать решение из каталога');
    }
  },
  getTariffs: async (serviceId) => {
    try {
      const response = await apiClient.get('/cabinet/subscriptions/tariffs', {
        params: serviceId ? { service_id: serviceId } : undefined,
      });
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить тарифы');
    }
  },
  createCharge: async (payload) => {
    try {
      const response = await apiClient.post('/cabinet/subscriptions/charges', payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось создать лицензию');
    }
  },
  updateCharge: async (id, payload) => {
    try {
      const response = await apiClient.put(`/cabinet/subscriptions/charges/${id}`, payload);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить лицензию');
    }
  },
  deleteCharge: async (id) => {
    try {
      const response = await apiClient.delete(`/cabinet/subscriptions/charges/${id}`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось удалить лицензию');
    }
  },
  getTrash: async () => {
    try {
      const response = await apiClient.get('/cabinet/subscriptions/trash');
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось загрузить корзину');
    }
  },
  restoreCharge: async (id) => {
    try {
      const response = await apiClient.post(`/cabinet/subscriptions/trash/${id}/restore`);
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось восстановить лицензию');
    }
  },
  updateNotifyEmail: async (chargeId, notify_email) => {
    try {
      const response = await apiClient.patch(
        `/cabinet/subscriptions/charges/${chargeId}/notify-email`,
        { notify_email }
      );
      return response.data;
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось сохранить email');
    }
  },
  export: async (params) => {
    try {
      const response = await apiClient.get('/cabinet/subscriptions/export', {
        params,
        responseType: 'blob',
      });
      saveSubscriptionFile(response, 'subscriptions.xlsx');
    } catch (error) {
      throw await subscriptionError(error, 'Не удалось выгрузить отчёт');
    }
  },
};

export default apiClient;
